import { collection, doc, limit, query, updateDoc, where, writeBatch } from 'firebase/firestore';
import { callFunction } from 'shared/services/functions';
import { db } from 'shared/services/firebase';
import { createDoc, getQueryOnce } from 'shared/services/firestore';
import { UserAccount } from 'shared/types/userAccount';
import {
  ExternalPlatformAccount,
  mapExternalPlatformAccount,
} from 'domains/experts/model/externalPlatformAccount';
import { MetaSrt, mapMetaSrt } from 'domains/experts/model/metaSrt';

/**
 * Expert-side reads for the partner-access + third-party-proxy screens.
 * Both queries mirror aligned's `FirestoreApiService` exactly: single-doc
 * lookup by `expertId`. Neither collection has a "deprecated" flag in aligned,
 * so no `where('deprecatedOn', '==', null)` — an empty query means "no such
 * account".
 */

const EXTERNAL_PLATFORM_ACCOUNTS = 'external_platform_accounts';
const META_SRTS = 'meta_srts';

function chunks<T>(values: T[], size: number): T[][] {
  const result: T[][] = [];
  for (let i = 0; i < values.length; i += size) result.push(values.slice(i, i + size));
  return result;
}

function uniqueNonEmpty(values: string[]): string[] {
  return Array.from(new Set(values.map((value) => value.trim()).filter(Boolean)));
}

export async function getExternalPlatformAccountByExpertId(
  expertId: string,
): Promise<ExternalPlatformAccount | null> {
  if (!expertId) return null;
  const rows = await getQueryOnce(
    query(collection(db, EXTERNAL_PLATFORM_ACCOUNTS), where('expertId', '==', expertId), limit(1)),
    mapExternalPlatformAccount,
  );
  return rows[0] ?? null;
}

export async function getExternalPlatformAccountsByExpertIds(
  expertIds: string[],
): Promise<Record<string, ExternalPlatformAccount>> {
  const uniqueIds = uniqueNonEmpty(expertIds);
  const entries: Array<[string, ExternalPlatformAccount]> = [];
  for (const chunk of chunks(uniqueIds, 30)) {
    const rows = await getQueryOnce(
      query(collection(db, EXTERNAL_PLATFORM_ACCOUNTS), where('expertId', 'in', chunk)),
      mapExternalPlatformAccount,
    );
    for (const row of rows) {
      if (row.expertId) entries.push([row.expertId, row]);
    }
  }
  return Object.fromEntries(entries);
}

export async function getExternalPlatformAccountsByEmails(emails: string[]): Promise<ExternalPlatformAccount[]> {
  const uniqueEmails = uniqueNonEmpty(emails).map((email) => email.toLowerCase());
  const accountsById = new Map<string, ExternalPlatformAccount>();
  for (const chunk of chunks(uniqueEmails, 30)) {
    const expertRows = await getQueryOnce(
      query(collection(db, EXTERNAL_PLATFORM_ACCOUNTS), where('expertEmail', 'in', chunk)),
      mapExternalPlatformAccount,
    );
    const alignedRows = await getQueryOnce(
      query(collection(db, EXTERNAL_PLATFORM_ACCOUNTS), where('alignedEmail', 'in', chunk)),
      mapExternalPlatformAccount,
    );
    for (const row of [...expertRows, ...alignedRows]) accountsById.set(row.id, row);
  }
  return Array.from(accountsById.values());
}

export async function getExternalPlatformAccountsByMetaSrtIds(srtIds: string[]): Promise<ExternalPlatformAccount[]> {
  const uniqueIds = uniqueNonEmpty(srtIds);
  const accountsById = new Map<string, ExternalPlatformAccount>();
  for (const chunk of chunks(uniqueIds, 30)) {
    const rows = await getQueryOnce(
      query(collection(db, EXTERNAL_PLATFORM_ACCOUNTS), where('metaSrtId', 'in', chunk)),
      mapExternalPlatformAccount,
    );
    for (const row of rows) accountsById.set(row.id, row);
  }
  return Array.from(accountsById.values());
}

type ExternalPlatformAccountUpdate = Partial<Pick<ExternalPlatformAccount, 'metaEnabled' | 'metaProxyEnabled' | 'metaSrtId' | 'mailgunRouteEnabled' | 'mailgunRouteId'>>;

interface MailgunRouteResponse {
  success?: boolean;
  routeId?: string | null;
  error?: string;
}

interface MailgunSuccessResponse {
  success?: boolean;
  error?: string;
}

function assertMailgunSuccess(response: MailgunSuccessResponse, fallbackMessage: string): void {
  if (response.success !== true) throw new Error(response.error || fallbackMessage);
}

export function buildExternalPlatformExpertName(user: UserAccount): string {
  return [user.firstNamePreferred || user.firstName, user.lastName].filter(Boolean).join(' ').trim() || user.email || user.id;
}

async function alignedEmailExists(email: string): Promise<boolean> {
  const rows = await getQueryOnce(
    query(collection(db, EXTERNAL_PLATFORM_ACCOUNTS), where('alignedEmail', '==', email.toLowerCase()), limit(1)),
    mapExternalPlatformAccount,
  );
  return rows.length > 0;
}

/**
 * The expert's routed aligned address, seeded from their **Firebase uid** —
 * aligned's `_generateUniqueAlignedEmail(firebaseId)`. Every one of the ~11k
 * addresses already issued follows that rule, so seeding from anything else
 * would make new experts the odd ones out in a live routing table.
 *
 * Widening the prefix (8 → 12 → whole uid) only happens on a collision, which
 * for a Firebase uid means a genuine duplicate rather than bad luck; the throw
 * at the end surfaces that instead of inventing an address.
 */
export async function generateUniqueAlignedExpertEmail(firebaseId: string): Promise<string> {
  const uid = firebaseId.trim();
  if (!uid) throw new Error('Cannot generate an aligned expert email without a Firebase id.');
  const candidates = [
    `expert-${uid.slice(0, Math.min(8, uid.length))}@aligned-experts4.com`,
    `expert-${uid.slice(0, Math.min(12, uid.length))}@aligned-experts4.com`,
    `expert-${uid}@aligned-experts4.com`,
  ].map((email) => email.toLowerCase());

  for (const email of candidates) {
    if (!(await alignedEmailExists(email))) return email;
  }
  throw new Error('Could not generate a unique aligned expert email address.');
}

export async function createExternalPlatformAccountForUser(user: UserAccount): Promise<ExternalPlatformAccount> {
  const existing = await getExternalPlatformAccountByExpertId(user.id);
  if (existing) throw new Error('This expert already has an external platform account.');

  const createdAt = new Date().toISOString();
  const alignedEmail = await generateUniqueAlignedExpertEmail(user.firebaseId);
  const accountData = {
    alignedEmail,
    firebaseId: user.firebaseId,
    expertId: user.id,
    expertName: buildExternalPlatformExpertName(user),
    expertEmail: user.email,
    mailgunRouteId: null,
    mailgunRouteEnabled: false,
    metaEnabled: false,
    metaProxyEnabled: false,
    metaSrtId: null,
    createdAt,
    updatedAt: null,
  };
  const created = await createDoc(EXTERNAL_PLATFORM_ACCOUNTS, accountData);
  return mapExternalPlatformAccount(created.id, created.data);
}

/** aligned's `createExternalPlatformAccountForUser` retry ladder. */
const CREATE_RETRY_DELAYS_MS = [500, 2_000, 8_000];

function isPermissionDenied(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { code?: unknown }).code === 'permission-denied';
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => { setTimeout(resolve, ms); });
}

/**
 * Idempotent counterpart to `createExternalPlatformAccountForUser`, ported from
 * aligned's onboarding path. Returns the existing account instead of throwing,
 * and retries transient Firestore failures — onboarding fires this without
 * awaiting it, so a blip that goes unretried costs the expert their aligned
 * email address until somebody notices.
 *
 * The existence check runs at the top of every attempt, which is what makes
 * retrying safe: if an earlier attempt's write landed but its response was
 * lost, the retry finds the account and returns it rather than minting a
 * second one.
 *
 * `permission-denied` is never retried. It means the caller's auth is gone
 * (signed out mid-flow, token revoked), which no amount of backoff recovers —
 * retrying only floods error reporting.
 */
export async function ensureExternalPlatformAccountForUser(user: UserAccount): Promise<ExternalPlatformAccount> {
  for (let attempt = 0; ; attempt += 1) {
    try {
      const existing = await getExternalPlatformAccountByExpertId(user.id);
      if (existing) return existing;
      return await createExternalPlatformAccountForUser(user);
    } catch (error) {
      if (isPermissionDenied(error) || attempt >= CREATE_RETRY_DELAYS_MS.length) throw error;
      await delay(CREATE_RETRY_DELAYS_MS[attempt]);
    }
  }
}

export async function updateExternalPlatformAccount(accountId: string, updates: ExternalPlatformAccountUpdate): Promise<string> {
  const updatedAt = new Date().toISOString();
  await updateDoc(doc(db, EXTERNAL_PLATFORM_ACCOUNTS, accountId), { ...updates, updatedAt });
  return updatedAt;
}

export async function batchUpdateExternalPlatformAccounts(accountIds: string[], updates: ExternalPlatformAccountUpdate): Promise<string> {
  const result = await batchUpdateExternalPlatformAccountsWithResult(accountIds, updates);
  if (result.failedIds.length > 0) {
    throw new Error(`Failed to update ${result.failedIds.length} external platform account${result.failedIds.length === 1 ? '' : 's'}.`);
  }
  return result.updatedAt;
}

/**
 * Batch update with the per-account outcome used by the aligned admin tool.
 * Firestore commits are atomic per 450-document chunk, so a failed chunk is
 * reported as failed rather than optimistically patching those rows locally.
 * Remaining chunks are still attempted to make partial progress visible.
 */
export interface BatchExternalPlatformUpdateResult {
  updatedAt: string;
  succeededIds: string[];
  failedIds: string[];
}

export async function batchUpdateExternalPlatformAccountsWithResult(
  accountIds: string[],
  updates: ExternalPlatformAccountUpdate,
): Promise<BatchExternalPlatformUpdateResult> {
  const ids = uniqueNonEmpty(accountIds);
  const updatedAt = new Date().toISOString();
  const succeededIds: string[] = [];
  const failedIds: string[] = [];
  for (const chunk of chunks(ids, 450)) {
    const batch = writeBatch(db);
    for (const accountId of chunk) {
      batch.update(doc(db, EXTERNAL_PLATFORM_ACCOUNTS, accountId), { ...updates, updatedAt });
    }
    try {
      await batch.commit();
      succeededIds.push(...chunk);
    } catch {
      failedIds.push(...chunk);
    }
  }
  return { updatedAt, succeededIds, failedIds };
}

export async function enableMailgunRouteForAccount(account: ExternalPlatformAccount): Promise<{ routeId: string; updatedAt: string }> {
  const response = await callFunction<MailgunRouteResponse>('mailgunCreateRoute', {
    alignedEmail: account.alignedEmail,
    forwardToEmail: account.expertEmail,
  });
  if (response.success !== true || !response.routeId) {
    throw new Error(response.error || `Failed to create Mailgun route for ${account.alignedEmail}.`);
  }
  const updatedAt = await updateExternalPlatformAccount(account.id, {
    mailgunRouteEnabled: true,
    mailgunRouteId: response.routeId,
  });
  return { routeId: response.routeId, updatedAt };
}

/** Port of aligned's campaign-onboarding helper. An external-platform account
 * must already exist; campaign movement does not silently invent one. */
export async function ensureMailgunAndMetaEnabled(expertId: string): Promise<ExternalPlatformAccount> {
  const account = await getExternalPlatformAccountByExpertId(expertId);
  if (!account) throw new Error(`No external platform account found for expert ${expertId}.`);
  if (!account.mailgunRouteEnabled) await enableMailgunRouteForAccount(account);
  if (!account.metaEnabled) await updateExternalPlatformAccount(account.id, { metaEnabled: true });
  return (await getExternalPlatformAccountByExpertId(expertId)) ?? {
    ...account,
    mailgunRouteEnabled: true,
    metaEnabled: true,
  };
}

export async function disableMailgunRouteForAccount(account: ExternalPlatformAccount): Promise<string> {
  if (account.mailgunRouteId) {
    try {
      assertMailgunSuccess(
        await callFunction<MailgunSuccessResponse>('mailgunDeleteRoute', { routeId: account.mailgunRouteId }),
        `Failed to delete Mailgun route for ${account.alignedEmail}.`,
      );
    } catch {
      // Aligned clears local route state even when Mailgun deletion fails, which
      // handles already-missing routes without leaving the account stuck enabled.
    }
  }
  return updateExternalPlatformAccount(account.id, {
    mailgunRouteEnabled: false,
    mailgunRouteId: null,
  });
}

export async function sendMailgunTestEmail(account: ExternalPlatformAccount): Promise<void> {
  assertMailgunSuccess(
    await callFunction<MailgunSuccessResponse>('mailgunTestEmailDelivery', {
      toEmail: account.alignedEmail,
      subject: 'Aligned Email Forwarding - System Check',
      body: `This is a test email sent to your Aligned email address (${account.alignedEmail}) to verify that forwarding to your personal email (${account.expertEmail}) is working correctly. No further action is needed from you. If you believe this was sent to you by mistake, you can respond to this email to reach our customer support team.`,
    }),
    `Failed to send test email to ${account.alignedEmail}.`,
  );
}

export async function getMetaSrtForExpert(expertId: string): Promise<MetaSrt | null> {
  if (!expertId) return null;
  const rows = await getQueryOnce(
    query(collection(db, META_SRTS), where('expertId', '==', expertId), limit(1)),
    mapMetaSrt,
  );
  return rows[0] ?? null;
}

/**
 * Persist the expert-side proxy result on an existing Meta SRT record. Aligned
 * updates only an existing record; proxy setup must not create or assign an
 * SRT as a side effect of the connectivity check.
 */
export async function updateMetaSrtProxyStatus(
  metaSrtId: string,
  proxyConfigured: boolean,
): Promise<void> {
  if (!metaSrtId) return;
  await updateDoc(doc(db, META_SRTS, metaSrtId), { proxyConfigured });
}
